import { useRef, useState, type FormEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser'
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { LOGIN_METHODS, type LoginMethod, type SessionUser } from '@jupi/shared'
import { ApiError, api } from '../api/client'
import { AuthCard } from './AuthCard'
import { METHOD_ICONS, SignInMethodInfo } from './SignInMethodInfo'
import { Turnstile, type TurnstileHandle } from './Turnstile'
import { isWebAuthnCancel, rememberMethod, rememberedMethod } from './signIn'

// Unset in local dev and any environment that hasn't configured a widget --
// the backend's verifyTurnstile() skips the check the same way when its
// secret is unset, so the two stay in sync without a widget here either.
export const TURNSTILE_SITE_KEY = import.meta.env?.VITE_TURNSTILE_SITE_KEY

export type SignedIn = (user: SessionUser, method: LoginMethod) => void

// The sign-in card, shared by the member app and the huolto app. Each app
// owns what happens after: `onSignedIn` is where it adopts the session and
// navigates. `forgotPassword` is a link of the app's own, because only the
// member app has a reset-password page.
//
// Three ways in: a password, a one-time email link or code, or a passkey.
// The tab opens on whichever this device used last (auth/signIn.ts); every
// method stays one tap away, and none of them asks the server anything
// about an address before a credential is presented.
export function LoginForm({
  onSignedIn,
  forgotPassword,
  title,
  subtitle,
}: {
  onSignedIn: SignedIn
  forgotPassword?: ReactNode
  title?: string
  subtitle?: string
}) {
  const { t } = useTranslation()
  const [method, setMethod] = useState<LoginMethod>(rememberedMethod)
  const signedIn: SignedIn = (user, used) => {
    rememberMethod(used)
    onSignedIn(user, used)
  }

  return (
    <AuthCard title={title} subtitle={subtitle}>
      <Tabs value={method} onChange={(_, value: LoginMethod) => setMethod(value)} variant="fullWidth" sx={{ mb: 3 }}>
        {LOGIN_METHODS.map((m) => (
          <Tab
            key={m}
            value={m}
            icon={<Icon icon={METHOD_ICONS[m]} width={20} />}
            iconPosition="start"
            label={t(`auth.method_${m}`)}
            sx={{ minHeight: 48, px: 1, textTransform: 'none' }}
          />
        ))}
      </Tabs>

      {method === 'password' ? <PasswordForm onSignedIn={signedIn} forgotPassword={forgotPassword} /> : null}
      {method === 'magic_link' ? <EmailLinkForm onSignedIn={signedIn} /> : null}
      {method === 'passkey' ? <PasskeyForm onSignedIn={signedIn} /> : null}

      <Stack sx={{ mt: 2 }}>
        <SignInMethodInfo />
      </Stack>
    </AuthCard>
  )
}

function useTurnstile() {
  const [token, setToken] = useState<string | undefined>(undefined)
  const ref = useRef<TurnstileHandle>(null)
  const widget = TURNSTILE_SITE_KEY ? (
    <Turnstile
      ref={ref}
      siteKey={TURNSTILE_SITE_KEY}
      onToken={setToken}
      onExpire={() => setToken(undefined)}
      onError={() => setToken(undefined)}
    />
  ) : null
  return {
    token,
    widget,
    ready: !TURNSTILE_SITE_KEY || Boolean(token),
    // A Turnstile token is single-use -- reset so retrying can get a fresh
    // one, whether this attempt spent it or it simply expired.
    reset: () => {
      ref.current?.reset()
      setToken(undefined)
    },
  }
}

function PasswordForm({ onSignedIn, forgotPassword }: { onSignedIn: SignedIn; forgotPassword?: ReactNode }) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const turnstile = useTurnstile()

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const { user } = await api.post<{ user: SessionUser }>('/api/auth/login', {
        email,
        password,
        turnstileToken: turnstile.token,
      })
      onSignedIn(user, 'password')
    } catch (err) {
      turnstile.reset()
      if (err instanceof ApiError && err.status === 429) {
        setError(t('auth.botCheckFailed'))
      } else {
        // The server answers identically for an unknown address and a wrong
        // password; say the same thing rather than guessing which it was.
        setError(
          err instanceof ApiError && err.status === 403 ? t('auth.accountInactive') : t('auth.invalidCredentials'),
        )
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Stack spacing={2}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        <TextField
          label={t('auth.email')}
          type="email"
          autoComplete="username"
          autoFocus
          required
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          label={t('auth.password')}
          type="password"
          autoComplete="current-password"
          required
          fullWidth
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {turnstile.widget}
        <Button type="submit" variant="contained" size="large" disabled={busy || !turnstile.ready} fullWidth>
          {busy ? t('auth.signingIn') : t('auth.signIn')}
        </Button>
        {forgotPassword}
      </Stack>
    </form>
  )
}

function EmailLinkForm({ onSignedIn }: { onSignedIn: SignedIn }) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const turnstile = useTurnstile()

  const request = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.post('/api/auth/magic-link', { email, turnstileToken: turnstile.token })
      setSentTo(email.trim().toLowerCase())
    } catch (err) {
      turnstile.reset()
      setError(err instanceof ApiError && err.status === 429 ? t('auth.tooManyAttempts') : t('common.error'))
    } finally {
      setBusy(false)
    }
  }

  const verify = async (event: FormEvent) => {
    event.preventDefault()
    if (!sentTo) return
    setBusy(true)
    setError(null)
    try {
      const { user } = await api.post<{ user: SessionUser }>('/api/auth/magic-link/code', { email: sentTo, code })
      onSignedIn(user, 'magic_link')
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0
      setError(
        status === 429 ? t('auth.tooManyAttempts') : status === 403 ? t('auth.accountInactive') : t('auth.codeInvalid'),
      )
    } finally {
      setBusy(false)
    }
  }

  if (sentTo) {
    return (
      <form onSubmit={verify} noValidate>
        <Stack spacing={2}>
          <Alert severity="success" icon={<Icon icon="mdi:email-check-outline" />}>
            {t('auth.linkSent', { email: sentTo })}
          </Alert>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            label={t('auth.codeLabel')}
            autoComplete="one-time-code"
            autoFocus
            required
            fullWidth
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^\d\s-]/g, '').slice(0, 9))}
            slotProps={{
              htmlInput: { inputMode: 'numeric', style: { letterSpacing: '0.4em', fontSize: '1.4rem', textAlign: 'center' } },
            }}
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={busy || code.replace(/\D/g, '').length !== 6}
          >
            {busy ? t('auth.signingIn') : t('auth.signInWithCode')}
          </Button>
          <Button
            size="small"
            onClick={() => {
              setSentTo(null)
              setCode('')
              setError(null)
              turnstile.reset()
            }}
          >
            {t('auth.useAnotherEmail')}
          </Button>
        </Stack>
      </form>
    )
  }

  return (
    <form onSubmit={request} noValidate>
      <Stack spacing={2}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        <TextField
          label={t('auth.email')}
          type="email"
          autoComplete="username"
          autoFocus
          required
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {turnstile.widget}
        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={busy || !email.includes('@') || !turnstile.ready}
        >
          {busy ? t('auth.sending') : t('auth.emailMeLink')}
        </Button>
      </Stack>
    </form>
  )
}

function PasskeyForm({ onSignedIn }: { onSignedIn: SignedIn }) {
  const { t } = useTranslation()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const supported = browserSupportsWebAuthn()

  const signIn = async () => {
    setBusy(true)
    setError(null)
    try {
      const options = await api.post<PublicKeyCredentialRequestOptionsJSON>('/api/auth/passkeys/login/options', {})
      const response = await startAuthentication({ optionsJSON: options })
      const { user } = await api.post<{ user: SessionUser }>('/api/auth/passkeys/login/verify', { response })
      onSignedIn(user, 'passkey')
    } catch (err) {
      if (isWebAuthnCancel(err)) setError(t('auth.passkeyCancelled'))
      else if (err instanceof ApiError && err.status === 429) setError(t('auth.tooManyAttempts'))
      else if (err instanceof ApiError && err.status === 403) setError(t('auth.accountInactive'))
      else setError(t('auth.passkeyFailed'))
    } finally {
      setBusy(false)
    }
  }

  if (!supported) return <Alert severity="warning">{t('auth.passkeyUnsupported')}</Alert>

  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Typography variant="body2" color="text.secondary">
        {t('auth.passkeyIntro')}
      </Typography>
      <Button
        variant="contained"
        size="large"
        fullWidth
        disabled={busy}
        onClick={signIn}
        startIcon={<Icon icon="mdi:fingerprint" />}
      >
        {busy ? t('auth.signingIn') : t('auth.passkeyButton')}
      </Button>
    </Stack>
  )
}
